import React, { useState } from 'react';
import { Button, Modal, Form, Input, Table, Space, Tag, Switch, Typography, Row, Col, DatePicker, Upload, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FileTextOutlined, LinkOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';
import { MenteeCertificateResponse, CreateMenteeCertificateRequest, UpdateMenteeCertificateRequest } from '../../../types';
import {
  compressImageToDataUrl,
  fileTooLargeForCertificate,
  normalizeCertificateImagePayload,
  certificateImageToDataUrlSrc,
} from '../../../utils/certificateImage';

const { Title } = Typography;
const { TextArea } = Input;

const CertificateTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<MenteeCertificateResponse | null>(null);
  const [form] = Form.useForm();
  const watchCertificateImage = Form.useWatch('certificateImage', form);
  const [imageProcessing, setImageProcessing] = useState(false);

  const { data: certificates = [], isLoading: loading } = useQuery({
    queryKey: menteeProfileQueryKeys.certificates,
    queryFn: async () => {
      const res = await apiService.getMenteeCertificates();
      if (!res.success) throw new Error(res.message || 'Sertifikalar yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMenteeCertificateRequest) => apiService.createMenteeCertificate(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.certificates });
        setModalVisible(false);
        form.resetFields();
      } else toast.error(res.message || 'Sertifika eklenemedi.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika eklenirken hata oluştu.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMenteeCertificateRequest }) =>
      apiService.updateMenteeCertificate(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.certificates });
        setModalVisible(false);
        setEditingCertificate(null);
        form.resetFields();
      } else toast.error(res.message || 'Sertifika güncellenemedi.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika güncellenirken hata oluştu.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMenteeCertificate(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Sertifika başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.certificates });
      } else toast.error(res.message || 'Sertifika silinemedi.');
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Sertifika silinirken hata oluştu.')),
  });

  const handleAdd = () => {
    setEditingCertificate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (certificate: MenteeCertificateResponse) => {
    setEditingCertificate(certificate);
    form.setFieldsValue({
      certificateName: certificate.certificateName,
      issuingOrganization: certificate.issuingOrganization,
      certificateNumber: certificate.certificateNumber,
      issueDate: dayjs(certificate.issueDate),
      expiryDate: certificate.expiryDate ? dayjs(certificate.expiryDate) : null,
      isExpired: certificate.isExpired,
      credentialUrl: certificate.credentialUrl,
      description: certificate.description,
      certificateImage: certificate.certificateImage
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const certificateData = {
        certificateName: values.certificateName,
        issuingOrganization: values.issuingOrganization,
        certificateNumber: values.certificateNumber,
        issueDate: values.issueDate.format('YYYY-MM-DD'),
        expiryDate: values.expiryDate ? values.expiryDate.format('YYYY-MM-DD') : null,
        isExpired: values.isExpired,
        credentialUrl: values.credentialUrl,
        description: values.description,
        certificateImage: normalizeCertificateImagePayload(values.certificateImage)
      };

      if (editingCertificate) {
        updateMutation.mutate({ id: editingCertificate.id, data: certificateData });
      } else {
        createMutation.mutate(certificateData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingCertificate(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  const handleImageUpload = async (file: File) => {
    if (fileTooLargeForCertificate(file)) {
      toast.error('Dosya çok büyük (en fazla 12 MB).');
      return false;
    }
    setImageProcessing(true);
    try {
      const dataUrl = await compressImageToDataUrl(file);
      form.setFieldsValue({ certificateImage: dataUrl });
    } catch {
      toast.error('Görsel işlenemedi; farklı bir dosya deneyin.');
    } finally {
      setImageProcessing(false);
    }
    return false;
  };

  const toImageSrc = certificateImageToDataUrlSrc;

  // Filtreleme
  const filteredCertificates = certificates.filter(certificate =>
    certificate.certificateName.toLowerCase().includes(searchText.toLowerCase()) ||
    (certificate.issuingOrganization && certificate.issuingOrganization.toLowerCase().includes(searchText.toLowerCase())) ||
    (certificate.certificateNumber && certificate.certificateNumber.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Sertifika Adı',
      dataIndex: 'certificateName',
      key: 'certificateName',
      sorter: (a: MenteeCertificateResponse, b: MenteeCertificateResponse) => a.certificateName.localeCompare(b.certificateName),
    },
    {
      title: 'Kurum',
      dataIndex: 'issuingOrganization',
      key: 'issuingOrganization',
      render: (organization: string) => organization ? <Tag color="blue">{organization}</Tag> : '-',
    },
    {
      title: 'Sertifika No',
      dataIndex: 'certificateNumber',
      key: 'certificateNumber',
      render: (number: string) => number ? (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {number}
        </span>
      ) : '-',
    },
    {
      title: 'Veriliş Tarihi',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
      sorter: (a: MenteeCertificateResponse, b: MenteeCertificateResponse) => 
        new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime(),
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (date: string, record: MenteeCertificateResponse) => {
        if (!date) return <Tag color="green">Süresiz</Tag>;
        const expiryDate = dayjs(date);
        const isExpired = expiryDate.isBefore(dayjs());
        return (
          <Tag color={isExpired ? 'red' : 'green'}>
            {expiryDate.format('DD/MM/YYYY')}
          </Tag>
        );
      },
    },
    {
      title: 'Durum',
      dataIndex: 'isExpired',
      key: 'isExpired',
      render: (isExpired: boolean, record: MenteeCertificateResponse) => {
        const expiryDate = record.expiryDate ? dayjs(record.expiryDate) : null;
        const isActuallyExpired = expiryDate ? expiryDate.isBefore(dayjs()) : false;
        
        return (
          <Tag color={isActuallyExpired ? 'red' : 'green'}>
            {isActuallyExpired ? 'Süresi Dolmuş' : 'Geçerli'}
          </Tag>
        );
      },
      filters: [
        { text: 'Geçerli', value: false },
        { text: 'Süresi Dolmuş', value: true }
      ],
      onFilter: (value: boolean | React.Key, record: MenteeCertificateResponse) => {
        const expiryDate = record.expiryDate ? dayjs(record.expiryDate) : null;
        const isActuallyExpired = expiryDate ? expiryDate.isBefore(dayjs()) : false;
        return isActuallyExpired === value;
      },
    },
    {
      title: 'Görsel',
      dataIndex: 'certificateImage',
      key: 'certificateImage',
      render: (image: string) => {
        if (!image) return '-';
        return (
          <Image
            width={40}
            height={30}
            src={toImageSrc(image)}
            style={{ borderRadius: '4px' }}
            preview={{
              mask: <EyeOutlined />
            }}
          />
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MenteeCertificateResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          {record.credentialUrl && (
            <Button
              type="text"
              icon={<LinkOutlined />}
              href={record.credentialUrl}
              target="_blank"
              style={{ color: 'var(--text-secondary)' }}
            />
          )}
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
    <PanelTabCard title="Sertifikalarım" description="Sertifikalarınızı ekleyin ve güncelleyin.">
        <TabListLoading loading={loading}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Sertifika
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Sertifika adı, kurum veya numara ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredCertificates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} sertifika`
          }}
        />
        </TabListLoading>
    </PanelTabCard>

      <Modal
        title={editingCertificate ? 'Sertifika Düzenle' : 'Yeni Sertifika'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={700}
        okText={editingCertificate ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="certificateName" 
                label="Sertifika Adı" 
                rules={[{ required: true, message: 'Sertifika adı gerekli' }]}
              >
                <Input placeholder="Örn: AWS Certified Solutions Architect" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="issuingOrganization" 
                label="Kurum"
              >
                <Input placeholder="Örn: Amazon Web Services" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="certificateNumber" 
                label="Sertifika Numarası"
              >
                <Input placeholder="Örn: AWS-123456" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="credentialUrl" 
                label="Doğrulama URL"
              >
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="issueDate" 
                label="Veriliş Tarihi" 
                rules={[
                  { required: true, message: 'Veriliş tarihi gerekli' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');
                      if (selectedDate.isAfter(today)) {
                        return Promise.reject(new Error('Veriliş tarihi bugünden sonra olamaz'));
                      }
                      const expiryDate = form.getFieldValue('expiryDate');
                      if (expiryDate && selectedDate.isAfter(dayjs(expiryDate))) {
                        return Promise.reject(new Error('Veriliş tarihi bitiş tarihinden sonra olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Veriliş tarihi"
                  disabledDate={(current) => current && current.isAfter(dayjs(), 'day')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="expiryDate" 
                label="Bitiş Tarihi"
                dependencies={['issueDate']}
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const issueDate = form.getFieldValue('issueDate');
                      if (issueDate && dayjs(value).isBefore(dayjs(issueDate))) {
                        return Promise.reject(new Error('Bitiş tarihi veriliş tarihinden önce olamaz'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Bitiş tarihi (opsiyonel)"
                  disabledDate={(current) => {
                    const issueDate = form.getFieldValue('issueDate');
                    if (issueDate && current) {
                      return current.isBefore(dayjs(issueDate), 'day');
                    }
                    return false;
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="isExpired" label="Süresi Dolmuş" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>

          <Form.Item label="Sertifika Görseli">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
              disabled={imageProcessing}
            >
              <Button icon={<FileTextOutlined />} loading={imageProcessing} disabled={imageProcessing}>
                {imageProcessing ? 'İşleniyor…' : 'Görsel Yükle'}
              </Button>
            </Upload>
            {watchCertificateImage && (
              <div style={{ marginTop: '8px' }}>
                <Image
                  width={100}
                  height={75}
                  src={toImageSrc(watchCertificateImage)}
                  style={{ borderRadius: '4px' }}
                />
                <div style={{ marginTop: '8px' }}>
                  <Button
                    size="small"
                    onClick={() => form.setFieldsValue({ certificateImage: undefined })}
                  >
                    Görseli Kaldır
                  </Button>
                </div>
              </div>
            )}
          </Form.Item>
          <Form.Item name="certificateImage" hidden>
            <Input />
          </Form.Item>

          <Form.Item 
            name="description" 
            label="Açıklama"
          >
            <TextArea 
              rows={3} 
              placeholder="Sertifika hakkında ek bilgiler..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CertificateTab;
